import React, { useState } from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';
import { Button } from '@eika/button';
import { useViewType, ViewType } from '@eika/hooks';

import { AccountView } from '../../components/AccountView';
import AccountViewSkeleton from '../../components/AccountView/AccountViewSkeleton';
import AccountBSUMessage from '../../components/AccountView/bsu/AccountBSUMessage';
import AccountBSUSkeleton from '../../components/AccountView/bsu/AccountBSUSkeleton';
import getAccountType, { getAccountViewTypeClassName } from '../../components/AccountView/util/getAccountType';
import AccountNotFound from '../../components/shared/AccountNotFound/AccountNotFound';
import { Content } from '../../components/shared/Content';
import { ErrorCardWrapper } from '../../components/shared/ErrorCardWrapper';
import { useAccount } from '../../fetchHooks/useAccount';
import useAccountId from '../../hooks/queryparams/useAccountId';
import useQueryParam from '../../hooks/queryparams/useQueryParam';
import useTranslation from '../../hooks/useTranslation';
import { TransactionList } from '../TransactionList';

const AccountViewApp: React.FC = () => {
    const accountId = useAccountId();
    const translated = useTranslation();
    const viewType = useViewType();
    const { data: account, ...accountState } = useAccount(accountId);
    const accountType = useQueryParam('accountType');
    const bsuAccountType = accountType === 'BSU' || accountType === 'BSU_PLUSS';

    const accountClassName = getAccountViewTypeClassName(getAccountType(account));

    const [noAccessToAccountException, setNoAccessToAccountException] = useState<
        RequestExceptionWithResponse | undefined
    >(undefined);

    if (accountState.error?.errorName === 'ACCOUNT_NOT_FOUND') {
        return <AccountNotFound />;
    }

    if (accountState.error || noAccessToAccountException) {
        const error = accountState.error || noAccessToAccountException;
        return (
            <Content>
                <ErrorCardWrapper exception={error} defaultError={translated.accountViewErrorMessage}>
                    {!noAccessToAccountException && (
                        <Button onClick={() => accountState.refetch()}>{translated.tryAgain}</Button>
                    )}
                </ErrorCardWrapper>
            </Content>
        );
    }

    return (
        <>
            {accountState.isFetched ? (
                <AccountView isLoading={accountState.isLoading} account={account} className={accountClassName} />
            ) : (
                <>
                    {accountType === 'BSU' ? (
                        <AccountBSUSkeleton />
                    ) : (
                        <AccountViewSkeleton className={accountClassName} />
                    )}
                </>
            )}

            {viewType !== ViewType.desktop && bsuAccountType && <AccountBSUMessage />}
            <TransactionList setNoAccessToAccountException={setNoAccessToAccountException} accountType={accountType} />
        </>
    );
};

export default AccountViewApp;
